import React, { Suspense } from "react";
import { Link } from "react-router-dom";
import { SEOHeadV2 } from "../../components/SEOHeadV2";
import { DemoDataBadge } from "../../components/DemoModeBanner";
import { SkeletonPanel } from "../../components/SkeletonPanel";
import { ErrorBoundary } from "../../components/ErrorBoundary";

/* Top-level Sports route pages — Iter 18 spec §1, §11.
 * Each page implements: SEO head, skeleton fallback, error boundary, and
 * AuditIQ brand standards. The global DemoModeBanner is rendered once at the
 * App.js level so it is not duplicated per route.
 *
 * The heavy interactive dashboards already exist under /auditiq-sports/* and
 * /demo/sports — these new top-level routes provide branded entry points and
 * route to the underlying experiences.
 */

const PageShell = ({ children, testId }) => (
  <div
    data-testid={testId}
    style={{
      minHeight: "100vh",
      background: "#0d1f3c",
      color: "#f4efe6",
      fontFamily: "Merriweather, Georgia, serif",
    }}
  >
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "32px 24px 80px" }}>
      <ErrorBoundary>
        <Suspense fallback={<SkeletonPanel title="Loading…" lines={5} />}>
          {children}
        </Suspense>
      </ErrorBoundary>
    </div>
  </div>
);

const titleStyle = {
  fontFamily: "Cinzel, Trajan Pro, Georgia, serif",
  color: "#c9a84c",
  fontSize: 36,
  letterSpacing: 2.5,
  textTransform: "uppercase",
  marginBottom: 6,
};
const lede = { color: "rgba(244,239,230,0.75)", fontSize: 14, lineHeight: 1.6, marginBottom: 24, maxWidth: 760 };
const card = {
  background: "#112244",
  border: "2px double #c9a84c",
  borderRadius: 8,
  padding: 22,
};
const linkBtn = {
  display: "inline-block",
  marginTop: 14,
  padding: "9px 18px",
  background: "#c9a84c",
  color: "#0d1f3c",
  borderRadius: 4,
  fontFamily: "Cinzel, Georgia, serif",
  fontSize: 11,
  letterSpacing: 1.4,
  textTransform: "uppercase",
  fontWeight: 600,
  textDecoration: "none",
};
const kpiBox = (color = "#c9a84c") => ({
  background: "#112244",
  border: `1px solid ${color}33`,
  borderRadius: 6,
  padding: 16,
});
const kpiNum = (color = "#c9a84c") => ({
  fontFamily: "Cinzel, Georgia, serif",
  color,
  fontSize: 28,
  fontWeight: 700,
});
const kpiLabel = {
  color: "rgba(244,239,230,0.65)",
  fontSize: 11,
  letterSpacing: 1.3,
  textTransform: "uppercase",
  marginTop: 4,
};

const KPIRow = ({ items }) => (
  <div
    data-testid="top-kpi-row"
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: 14,
      marginBottom: 24,
    }}
  >
    {items.map((it) => (
      <div key={it.label} style={kpiBox(it.color)}>
        <div style={kpiNum(it.color)}>{it.value}</div>
        <div style={kpiLabel}>{it.label}</div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------- /dashboard
export const TopDashboard = () => (
  <PageShell testId="page-dashboard">
    <SEOHeadV2 />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 6 }}>
      <h1 style={titleStyle} data-testid="page-dashboard-title">Command Dashboard</h1>
      <Link to="/security" data-testid="dashboard-security-badge"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px",
          border: "1px solid rgba(201,168,76,0.55)", color: "#c9a84c", borderRadius: 4,
          fontFamily: "Cinzel, Georgia, serif", fontSize: 10, letterSpacing: 1.2,
          textTransform: "uppercase", fontWeight: 600, textDecoration: "none" }}>
        ✓ Security &amp; Compliance
      </Link>
    </div>
    <div style={lede}>
      Real-time athlete performance, Health IQ risk monitoring, and compliance oversight for institutional
      athletic programs. Use the panels below to navigate the AuditIQ Sports control surface.
    </div>
    <KPIRow
      items={[
        { value: "208", label: "Athletes monitored", color: "#c9a84c" },
        { value: "44", label: "Active teams", color: "#c9a84c" },
        { value: "4", label: "Anchor institutions", color: "#2ecc71" },
        { value: "20", label: "Public data sources", color: "#c9a84c" },
      ]}
    />
    <DemoDataBadge />
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", marginTop: 16 }}>
      <div style={card} data-testid="dashboard-tile-command-center">
        <div style={{ color: "#c9a84c", fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, fontSize: 13, textTransform: "uppercase" }}>
          Command Center
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginTop: 8 }}>
          Athletic Director executive view — readiness pulse, system health, and live risk indicators.
        </p>
        <Link to="/auditiq-sports/command-center" style={linkBtn} data-testid="dashboard-tile-command-center-link">Open</Link>
      </div>
      <div style={card} data-testid="dashboard-tile-demo">
        <div style={{ color: "#c9a84c", fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, fontSize: 13, textTransform: "uppercase" }}>
          Demo Intelligence
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginTop: 8 }}>
          Public-data demo dashboard with seeded athletes, Health IQ scoring, and PDF export.
        </p>
        <Link to="/demo/sports" style={linkBtn} data-testid="dashboard-tile-demo-link">Open</Link>
      </div>
      <div style={card} data-testid="dashboard-tile-healthiq">
        <div style={{ color: "#c9a84c", fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, fontSize: 13, textTransform: "uppercase" }}>
          Health IQ
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginTop: 8 }}>
          Recovery, Fatigue, Risk Tier scoring and seven-day workload sparklines for trainers.
        </p>
        <Link to="/health-iq" style={linkBtn} data-testid="dashboard-tile-healthiq-link">Open</Link>
      </div>
    </div>
  </PageShell>
);

// ------------------------------------------------------------------- /teams
export const TopTeams = () => (
  <PageShell testId="page-teams">
    <SEOHeadV2 />
    <h1 style={titleStyle} data-testid="page-teams-title">Teams</h1>
    <div style={lede}>
      Browse and manage athletic team rosters, schedules, and performance analytics across all
      sports programs on the AuditIQ platform.
    </div>
    <KPIRow
      items={[
        { value: "44", label: "Teams active" },
        { value: "4", label: "Sports tracked" },
        { value: "4", label: "Institutions" },
        { value: "208", label: "Athletes" },
      ]}
    />
    <DemoDataBadge />
    <div style={{ ...card, marginTop: 16 }} data-testid="teams-listing-card">
      <div style={{ color: "#c9a84c", fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, fontSize: 14, textTransform: "uppercase", marginBottom: 10 }}>
        Anchor institutions
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {[
          { name: "University of Florida Gators", level: "NCAA Division I · SEC", url: "/auditiq-sports/football" },
          { name: "Florida State University Seminoles", level: "NCAA Division I · ACC", url: "/auditiq-sports/football" },
          { name: "University of Miami Hurricanes", level: "NCAA Division I · ACC", url: "/auditiq-sports/football" },
          { name: "Dr. Phillips High School Panthers", level: "FHSAA Class 4M", url: "/auditiq-sports/football" },
        ].map((t) => (
          <li
            key={t.name}
            data-testid={`team-row-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            style={{
              padding: "12px 0",
              borderBottom: "1px solid rgba(201,168,76,0.18)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 14, color: "#f4efe6" }}>{t.name}</div>
              <div style={{ fontSize: 11, color: "rgba(244,239,230,0.55)", marginTop: 2 }}>{t.level}</div>
            </div>
            <Link to={t.url} style={{ color: "#c9a84c", fontSize: 12, letterSpacing: 1, fontFamily: "Cinzel, Georgia, serif" }}>
              View →
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </PageShell>
);

// --------------------------------------------------------------- /athletes
export const TopAthletes = () => (
  <PageShell testId="page-athletes">
    <SEOHeadV2 />
    <h1 style={titleStyle} data-testid="page-athletes-title">Athletes</h1>
    <div style={lede}>
      Track individual athlete performance, Health IQ readiness scores, recruiting status, and
      compliance standing in one unified profile.
    </div>
    <KPIRow
      items={[
        { value: "208", label: "Athletes profiled" },
        { value: "128", label: "Anchor (verified)" },
        { value: "—", label: "Critical risk", color: "#8e44ad" },
        { value: "—", label: "Available now", color: "#2ecc71" },
      ]}
    />
    <DemoDataBadge />
    <div style={{ ...card, marginTop: 16 }} data-testid="athletes-cta-card">
      <div style={{ color: "#c9a84c", fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, fontSize: 13, textTransform: "uppercase" }}>
        Drill-down
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginTop: 8 }}>
        Open the Demo Intelligence Dashboard to filter, search, and click into an individual
        athlete profile with public-source attribution links.
      </p>
      <Link to="/demo/sports" style={linkBtn} data-testid="athletes-open-demo">Open Demo Intelligence</Link>
    </div>
  </PageShell>
);

// --------------------------------------------------------------- /coaches
export const TopCoaches = () => (
  <PageShell testId="page-coaches">
    <SEOHeadV2 />
    <h1 style={titleStyle} data-testid="page-coaches-title">Coaches</h1>
    <div style={lede}>
      Coach management dashboard for staff assignments, team oversight, and performance reporting
      across university and high school programs.
    </div>
    <KPIRow
      items={[
        { value: "44", label: "Head coaches" },
        { value: "4", label: "Sports" },
        { value: "—", label: "Open requisitions" },
        { value: "—", label: "Compliance attestations due" },
      ]}
    />
    <DemoDataBadge />
    <div style={{ ...card, marginTop: 16 }} data-testid="coaches-cta-card">
      <div style={{ color: "#c9a84c", fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, fontSize: 13, textTransform: "uppercase" }}>
        Coach dashboards
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginTop: 8 }}>
        Coaching staff can sign in to view team-scoped rosters, alerts, and Health IQ summaries.
      </p>
      <Link to="/auditiq-sports/coach-dashboard" style={linkBtn} data-testid="coaches-open-dashboard">Open Coach Dashboard</Link>
    </div>
  </PageShell>
);

// --------------------------------------------------------------- /health-iq
export const TopHealthIQ = () => (
  <PageShell testId="page-health-iq">
    <SEOHeadV2 />
    <h1 style={titleStyle} data-testid="page-health-iq-title">Health IQ</h1>
    <div style={lede}>
      Athlete readiness, fatigue risk, recovery scores, and availability — purpose-built for
      certified athletic trainers and athletic directors. HIPAA-aware: medical record fields are
      never exposed in demo mode.
    </div>
    <div
      style={{
        display: "grid",
        gap: 14,
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        marginBottom: 24,
      }}
    >
      {[
        { label: "Low", color: "#2ecc71" },
        { label: "Moderate", color: "#f39c12" },
        { label: "High", color: "#e74c3c" },
        { label: "Critical", color: "#8e44ad" },
      ].map((tier) => (
        <div key={tier.label} style={{ ...kpiBox(tier.color), textAlign: "center" }} data-testid={`health-iq-tier-${tier.label.toLowerCase()}`}>
          <div style={{ ...kpiNum(tier.color), fontSize: 22 }}>{tier.label}</div>
          <div style={kpiLabel}>Risk tier</div>
        </div>
      ))}
    </div>
    <DemoDataBadge />
    <div style={{ ...card, marginTop: 16 }} data-testid="health-iq-cta-card">
      <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginBottom: 12 }}>
        Open a Health IQ-enabled athlete profile to view 7-day workload sparklines, recovery score,
        fatigue index, and computed risk tier.
      </p>
      <Link to="/auditiq-sports/healthiq" style={linkBtn} data-testid="health-iq-open">Health IQ Overview</Link>
    </div>
  </PageShell>
);

// --------------------------------------------------------------- /compliance
export const TopCompliance = () => (
  <PageShell testId="page-compliance">
    <SEOHeadV2 />
    <h1 style={titleStyle} data-testid="page-compliance-title">Compliance</h1>
    <div style={lede}>
      NCAA and FHSAA compliance workflows, eligibility actions, audit trails, and institutional
      oversight. Every approval is signed and timestamped to a system auditor log.
    </div>
    <KPIRow
      items={[
        { value: "—", label: "Pending eligibility reviews" },
        { value: "—", label: "Open audit findings", color: "#f39c12" },
        { value: "—", label: "Signed attestations" },
        { value: "100%", label: "Audit-trail coverage", color: "#2ecc71" },
      ]}
    />
    <div style={{ ...card, marginTop: 16 }} data-testid="compliance-cta-card">
      <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginBottom: 12 }}>
        Compliance officers and athletic directors can review live workflows in the Sports
        compliance module.
      </p>
      <Link to="/auditiq-sports/compliance" style={linkBtn} data-testid="compliance-open">Open Compliance Module</Link>
    </div>
  </PageShell>
);

// --------------------------------------------------------------- /reports
export const TopReports = () => (
  <PageShell testId="page-reports">
    <SEOHeadV2 />
    <h1 style={titleStyle} data-testid="page-reports-title">Reports</h1>
    <div style={lede}>
      Generate and export institutional performance reports, Health IQ risk summaries, and
      compliance documentation. Branded PDF and CSV outputs available.
    </div>
    <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
      <div style={card} data-testid="reports-tile-demo-pdf">
        <div style={{ color: "#c9a84c", fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, fontSize: 13, textTransform: "uppercase" }}>
          Demo Intelligence PDF
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginTop: 8 }}>
          Full demo report — anchor universities, top prospects, Health IQ risk board, recruiting
          board, and crawl source freshness.
        </p>
        <Link to="/demo/sports" style={linkBtn} data-testid="reports-tile-demo-pdf-link">Open Demo &amp; Export</Link>
      </div>
      <div style={card} data-testid="reports-tile-command-center">
        <div style={{ color: "#c9a84c", fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, fontSize: 13, textTransform: "uppercase" }}>
          Command Center Snapshot
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: "#f4efe6", marginTop: 8 }}>
          AD executive snapshot — readiness pulse and high-level risk indicators.
        </p>
        <Link to="/auditiq-sports/command-center" style={linkBtn} data-testid="reports-tile-command-center-link">Open Command Center</Link>
      </div>
    </div>
  </PageShell>
);
